/* eslint-disable no-underscore-dangle */
import * as _ from "underscore";
import typeToReducer from "type-to-reducer";

import { FIELD_TYPE, WS_DEF, getFieldDef } from "./wsDefs";
import { IFieldDef, IWorksheetState, IState } from "./interfaces";
import { errWithInfo, warn } from "./debug";
import { getWsById, hasWsId, withoutWs } from "./wsHelpers";

import {
  WS_GET_LOCK,
  WS_CREATE,
  WS_IMPORT,
  WS_FETCH,
  WS_UPDATE,
  WS_DELETE_FROM_FIELD,
} from "./types";

/* initial state */

const initialState: IState = {
  worksheets: [],
};

const initialWsState: IWorksheetState = {
  id: null,
  name: null,
  appId: null,
  locked: null,
  instance: null,
};

/* helpers */

// NOTE: jjw: Don't use getFieldDefIfExists() for anything other than the
// helpers below. The safer/standard app-wide function is getFieldDef(); this
// version here is more lax, and is used when first iterating through worksheet
// instances from the backend, where we now don't want to blow up if the backend
// sends fields that we've forgotten to add in the front-end defs. After this
// step, though, we want those assurances for the rest of the runtime.

export const getFieldDefIfExists = (wsDefs, wsName, field): IFieldDef => {
  const wsDef = wsDefs[wsName];
  if (!wsDef) {
    warn(`getFieldDefIfExists: skipping worksheet ${wsName}; no def found`);
    return null;
  }
  const fieldDef: IFieldDef[] = _.where(wsDef, { field });
  if (!fieldDef.length) {
    warn(
      `getFieldDefIfExists: skipping field ${field} in worksheet def ${wsName}; field not found`,
    );
    return null;
  }
  if (fieldDef.length > 1) {
    throw new Error(
      `getFieldDefIfExists failed: more than one field ${field} in worksheet def ${wsName}`,
    );
  }
  return fieldDef[0];
};

// NOTE: jjw: Currently, the backend emits worksheet instances as a nested tree.
// So, for example, fetching the loan file (top-most worksheet) will return an
// instance of that worksheet with all child instances attached. The front-end,
// on the other hand, stores worksheet instances in a flat array and then uses
// worksheet IDs to show references between them (for fields whose types are
// worksheets or slices of worksheets). The helper functions below take the API
// format and translate it to the front-end format. Once we redesign the API to
// emit a flat list of instances, we can remove these helpers and simplify.

const shallowInstance = (wsDefs, wsName, instance) => {
  const inst = _.mapObject(instance, (value, field) => {
    const fieldDef = getFieldDefIfExists(wsDefs, wsName, field);

    // if field not found in def, then mark to strip out key-value pair
    if (fieldDef === null) {
      return undefined; // field not found in def, so mark to strip out key
    }

    // also strip out key-value pair if field type is a worksheet or worksheet
    // slice, and we don't know the type of worksheet the field references
    if (
      (fieldDef.type === FIELD_TYPE.WS_SLICE || fieldDef.type === FIELD_TYPE.WS) &&
      !wsDefs.hasOwnProperty(fieldDef.wsName)
    ) {
      warn(
        `shallowInstance: skipping field ${field} in worksheet def ${wsName}; don't know ${fieldDef.wsName}`,
      );
      return undefined;
    }

    // otherwise, map instances to their uuids and return values unchanged
    if (fieldDef.type === FIELD_TYPE.WS_SLICE) {
      return _.map(value, (v: any) => v.id);
    }
    if (fieldDef.type === FIELD_TYPE.WS) {
      return value === null ? null : value.id;
    }
    return value;
  });

  return _.omit(inst, value => value === undefined); // strip any field-value pairs not in def
};

const _deepInstanceToWsList = (wsDefs, appId, wsName, instance, wsList = []) => {
  wsList.push({
    ...initialWsState,
    id: instance.id,
    name: wsName,
    appId,
    locked: false,
    instance: shallowInstance(wsDefs, wsName, instance),
  });

  Object.keys(instance).forEach(field => {
    const fieldDef = getFieldDefIfExists(wsDefs, wsName, field);

    // can skip traversing field if not in def
    if (fieldDef === null) {
      return;
    }

    // can also skip if it's a worksheet or worksheet slice and don't know type
    if (
      (fieldDef.type === FIELD_TYPE.WS_SLICE || fieldDef.type === FIELD_TYPE.WS) &&
      !wsDefs.hasOwnProperty(fieldDef.wsName)
    ) {
      return;
    }

    // otherwise traverse worksheet and worksheet slice types as normal
    if (fieldDef.type === FIELD_TYPE.WS_SLICE) {
      for (let i = 0, l = instance[field].length; i < l; i += 1) {
        _deepInstanceToWsList(wsDefs, appId, fieldDef.wsName, instance[field][i], wsList);
      }
    } else if (fieldDef.type === FIELD_TYPE.WS && instance[field] !== null) {
      _deepInstanceToWsList(wsDefs, appId, fieldDef.wsName, instance[field], wsList);
    }
  });

  return wsList;
};

export const forUnitTestsOnly = {
  _deepInstanceToWsList,
};

export const deepInstanceToWsList = (appId, wsName, instance) =>
  _deepInstanceToWsList(WS_DEF, appId, wsName, instance);

/* reducer */

// TODO: jjw: integrity checks:
// -- assert that worksheet state id matches instance id
// -- assert that worksheet instance matches schema from def
// -- assert that string values for enums are in provided list

// TODO: jjw: we don't yet support:
// -- handling field migrations that could happen across worksheet versions
// -- using versions in any other meaningful way (e.g., providing history)

export default typeToReducer(
  {
    [WS_GET_LOCK]: (state, { data, type }) => {
      const { appId, wsId } = data;

      const ws = getWsById(state.worksheets, wsId);
      if (!ws) {
        errWithInfo(appId, type, `cannot find ${wsId}`);
      }

      // TODO: jjw: checks are also in action; move all asserts out of reducer?
      if (ws.appId !== appId) {
        errWithInfo(appId, type, `${wsId} belongs to loan app ${ws.appId}`);
      }
      if (ws.locked === null) {
        errWithInfo(appId, WS_GET_LOCK, `${wsId} not yet initialized`);
      }
      if (ws.locked) {
        errWithInfo(appId, WS_GET_LOCK, `${wsId} already locked`);
      }

      const update = {
        ...ws,
        locked: true,
      };

      return {
        ...state,
        worksheets: [...withoutWs(state.worksheets, wsId), update],
      };
    },
    [WS_CREATE]: {
      PENDING: (state, { meta, type }) => {
        const { appId, wsName, tempId } = meta;

        if (hasWsId(state.worksheets, tempId)) {
          errWithInfo(appId, type, `${tempId} already exists`);
        }

        const update = {
          ...initialWsState,
          id: tempId,
          name: wsName,
          appId,
          locked: true,
        };

        return {
          ...state,
          worksheets: [...Array.from(state.worksheets), update],
        };
      },
      SUCCESS: (state, { meta, type, data }) => {
        const { appId, tempId } = meta;

        const ws = getWsById(state.worksheets, tempId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${tempId}`);
        }
        if (ws.appId !== appId) {
          errWithInfo(appId, type, `${tempId} belongs to loan app ${ws.appId}`);
        }

        const update = {
          ...ws,
          id: data.worksheet.id,
          locked: false,
          instance: data.worksheet,
        };

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, tempId), update],
        };
      },
      FAILURE: (state, { type, meta, error }) => {
        const { appId, tempId } = meta;

        alert(error.meta.error_detail);
        errWithInfo(appId, type, `server error on ${tempId}: ${error}`);
      },
    },
    [WS_IMPORT]: {
      PENDING: (state, { type, meta }) => {
        const { appId, wsId } = meta;

        const ws = getWsById(state.worksheets, wsId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${wsId}`);
        }

        const update = {
          ...ws,
          locked: true,
        };

        return { ...state, worksheets: [...withoutWs(state.worksheets, wsId), update] };
      },
      SUCCESS: (state, { type, meta, data }) => {
        const { appId, wsName, wsId } = meta;

        const ws = getWsById(state.worksheets, wsId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${wsId}`);
        }
        if (ws.appId !== appId) {
          errWithInfo(appId, type, `${wsId} belongs to loan app ${ws.appId}`);
        }

        const update = deepInstanceToWsList(appId, wsName, data.worksheet);

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, _.pluck(update, "id")), ...update],
        };
      },
      FAILURE: (state, { type, meta, error }) => {
        const { appId, wsId } = meta;

        alert(error.meta.error_detail);
        errWithInfo(appId, type, `server error on ${wsId}: ${error}`);
      },
    },
    [WS_FETCH]: {
      PENDING: (state, { type, meta }) => {
        const { appId, wsName, wsId } = meta;

        const ws = getWsById(state.worksheets, wsId);

        let update;
        if (ws) {
          update = {
            ...ws,
            locked: true,
          };
        } else {
          update = {
            ...initialWsState,
            id: wsId,
            name: wsName,
            appId,
            locked: true,
          };
        }

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, wsId), update],
        };
      },
      SUCCESS: (state, { type, meta, data }) => {
        const { appId, wsName, wsId } = meta;

        const ws = getWsById(state.worksheets, wsId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${wsId}`);
        }
        if (ws.appId !== appId) {
          errWithInfo(appId, type, `${wsId} belongs to loan app ${ws.appId}`);
        }

        const update = deepInstanceToWsList(appId, wsName, data.worksheet);

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, _.pluck(update, "id")), ...update],
        };
      },
      FAILURE: (state, { type, meta, error }) => {
        const { appId, wsId } = meta;

        alert(error.meta.error_detail);
        errWithInfo(appId, type, `server error on ${wsId}: ${error}`);
      },
    },
    [WS_UPDATE]: {
      PENDING: (state, { type, meta }) => {
        const { appId, wsId, edit } = meta;

        const ws = getWsById(state.worksheets, wsId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${wsId}`);
        }
        if (ws.appId !== appId) {
          errWithInfo(appId, type, `${wsId} belongs to loan app ${ws.appId}`);
        }
        if (!ws.instance || !ws.instance.hasOwnProperty(edit.field)) {
          errWithInfo(appId, type, `${wsId} has no field ${edit.field}`);
        }

        const fieldDef = getFieldDef(ws.name, edit.field);

        // TODO: jjw: make slice updates a separate action
        let val;
        if (fieldDef.type === FIELD_TYPE.WS_SLICE) {
          val = [...ws.instance[edit.field], edit.value];
        } else {
          val = edit.value;
        }

        const update = {
          ...ws,
          locked: true,
          instance: {
            ...ws.instance,
            [edit.field]: val,
          },
        };

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, wsId), update],
        };
      },
      SUCCESS: (state, { type, meta, data }) => {
        const { appId, wsId } = meta;

        const ws = getWsById(state.worksheets, wsId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${wsId}`);
        }
        if (ws.appId !== appId) {
          errWithInfo(appId, type, `${wsId} belongs to loan app ${ws.appId}`);
        }

        let update = deepInstanceToWsList(appId, ws.name, data.worksheet);
        if (data.additional && data.additional.length) {
          data.additional.forEach(additionalWs => {
            const existingWs = getWsById(state.worksheets, additionalWs.id);
            if (!existingWs) {
              errWithInfo(
                appId,
                type,
                `cannot get dependent worksheet ${additionalWs.id} to update`,
              );
            }
            update = [...update, ...deepInstanceToWsList(appId, existingWs.name, additionalWs)];
          });
        }

        // de-dupe the list since through child/parent relationships a worksheet could be
        // included multiple times in the worksheets API response.
        update = _.uniq(update, ws => ws.id);

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, _.pluck(update, "id")), ...update],
        };
      },
      FAILURE: (state, { type, meta, error }) => {
        const { appId, wsId } = meta;

        alert(error.meta.error_detail);
        errWithInfo(appId, type, `server error on ${wsId}: ${error}`);
      },
    },
    [WS_DELETE_FROM_FIELD]: {
      PENDING: (state, { type, meta }) => {
        const { appId, wsId, edit } = meta;

        const ws = getWsById(state.worksheets, wsId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${wsId}`);
        }
        if (ws.appId !== appId) {
          errWithInfo(appId, type, `${wsId} belongs to loan app ${ws.appId}`);
        }
        if (!ws.instance || !ws.instance.hasOwnProperty(edit.field)) {
          errWithInfo(appId, type, `${wsId} has no field ${edit.field}`);
        }

        const fieldDef = getFieldDef(ws.name, edit.field);
        if (fieldDef.type !== FIELD_TYPE.WS_SLICE) {
          errWithInfo(appId, type, `${edit.field} on ${wsId} not a slice`);
        }

        const val = [...ws.instance[edit.field]].splice(edit.index, 1);

        const update = {
          ...ws,
          locked: true,
          instance: {
            ...ws.instance,
            [edit.field]: val,
          },
        };

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, wsId), update],
        };
      },
      SUCCESS: (state, { type, meta, data }) => {
        const { appId, wsId } = meta;

        const ws = getWsById(state.worksheets, wsId);
        if (!ws) {
          errWithInfo(appId, type, `cannot find ${wsId}`);
        }
        if (ws.appId !== appId) {
          errWithInfo(appId, type, `${wsId} belongs to loan app ${ws.appId}`);
        }

        let update = deepInstanceToWsList(appId, ws.name, data.worksheet);
        if (data.additional && data.additional.length) {
          data.additional.forEach(additionalWs => {
            const existingWs = getWsById(state.worksheets, additionalWs.id);
            if (!existingWs) {
              errWithInfo(
                appId,
                type,
                `cannot get dependent worksheet ${additionalWs.id} to update`,
              );
            }
            update = [...update, ...deepInstanceToWsList(appId, existingWs.name, additionalWs)];
          });
        }

        // de-dupe the list since through child/parent relationships a worksheet could be
        // included multiple times in the worksheets API response.
        update = _.uniq(update, ws => ws.id);

        return {
          ...state,
          worksheets: [...withoutWs(state.worksheets, _.pluck(update, "id")), ...update],
        };
      },
      FAILURE: (state, { type, meta, error }) => {
        const { appId, wsId } = meta;

        alert(error.meta.error_detail);
        errWithInfo(appId, type, `server error on ${wsId}: ${error}`);
      },
    },
  } as any,
  initialState,
);
